import { useState } from "react";

export function useTenantsData(supabase, landlordId, reload) {
  const [tenants, setTenants] = useState([]);
  const [searchTenant, setSearchTenant] = useState("");
  const [showAddTenant, setShowAddTenant] = useState(false);
  const [showTenantHistory, setShowTenantHistory] = useState(null);
  const [newTenant, setNewTenant] = useState({ name: "", property: "", address: "", city: "Pittsburg", state: "CA", zip: "", email: "", phone: "", lease_start: "", lease_end: "", rent: "", property_type: "House", status: "active", notes: "", due_day: "1", is_complimentary: false, complimentary_notes: "", tenant_type: "standard", non_paying_subtype: "", expense_notes: "" });
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [editTenant, setEditTenant] = useState(null);

  const [tenantPhotos, setTenantPhotos] = useState([]);
  const [photoUploadFile, setPhotoUploadFile] = useState(null);
  const [photoType, setPhotoType] = useState("move_in");
  const [photoLabel, setPhotoLabel] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);

  const [rentIncreases, setRentIncreases] = useState([]);
  const [showAddRentIncrease, setShowAddRentIncrease] = useState(false);
  const [newRentIncrease, setNewRentIncrease] = useState({ tenant: "", effective_date: "", new_rent: "", notes: "" });

  // ── TENANTS ──────────────────────────────────────────────────────────────────
  const deleteTenant = async (t) => {
    if (!window.confirm(`Delete ${t.name}? This cannot be undone.`)) return;
    await supabase.from("tenants").delete().eq("name", t.name);
    reload();
  };

  const addTenant = async () => {
    if (!newTenant.name) return;
    setShowAddTenant(false);
    const tenantPayload = { ...newTenant, rent: newTenant.rent ? Math.round(parseFloat(newTenant.rent)) : null, lease_start: newTenant.lease_start || null, lease_end: newTenant.lease_end || null, due_day: parseInt(newTenant.due_day || 1), user_id: landlordId };
    const { error } = await supabase.from("tenants").insert([tenantPayload]);
    if (error) { alert("Error: " + error.message); return; }
    supabase.from("notifications").insert([{
      user_id: landlordId, type: "tenant_added",
      message: `${tenantPayload.name} was added to ${tenantPayload.address || "your portfolio"}`,
      link: "Tenants",
    }]).then(({ error: nErr }) => { if (nErr) console.warn("[notifications] tenant_added insert failed:", nErr.message); });
    if (newTenant.address) {
      const addr = newTenant.address.trim();
      const { data: existing } = await supabase.from("properties").select("id").eq("user_id", landlordId).ilike("address", addr).maybeSingle();
      if (!existing) await supabase.from("properties").insert([{ address: addr, units: 1, user_id: landlordId }]);
    }

    // Send welcome email + tenant portal invite link (non-fatal: failure doesn't block the add)
    console.log("[addTenant] email check →", {
      email: tenantPayload.email,
      is_complimentary: tenantPayload.is_complimentary,
      tenant_type: tenantPayload.tenant_type,
    });
    if (tenantPayload.email) {
      console.log("[addTenant] calling send-tenant-welcome for", tenantPayload.email);
      try {
        const { data: fnData, error: fnErr } = await supabase.functions.invoke("send-tenant-welcome", {
          body: { tenant: tenantPayload, landlordId, origin: window.location.origin },
        });
        if (fnErr) {
          // supabase-js wraps non-2xx as a FunctionsHttpError; try to read the real body
          let errMsg = fnErr.message;
          try { const body = await fnErr.context?.json?.(); if (body?.error) errMsg = body.error; } catch {}
          console.warn("[addTenant] send-tenant-welcome error:", errMsg);
        } else if (fnData?.error) {
          console.warn("[addTenant] send-tenant-welcome function error:", fnData.error);
        } else {
          console.log("[addTenant] welcome email sent ✓ id:", fnData?.emailId);
        }
      } catch (invokeEx) {
        console.warn("[addTenant] send-tenant-welcome exception:", String(invokeEx));
      }
    } else {
      console.log("[addTenant] skipping welcome email — no email address or complimentary/non-paying tenant");
    }

    setNewTenant({ name: "", property: "", address: "", city: "Pittsburg", state: "CA", zip: "", email: "", phone: "", lease_start: "", lease_end: "", rent: "", property_type: "House", status: "active", notes: "", due_day: "1", is_complimentary: false, complimentary_notes: "", tenant_type: "standard", non_paying_subtype: "", expense_notes: "" });
    reload();
  };

  const saveTenantEdit = async () => {
    if (!editTenant.name) return;
    const payload = {
      name: editTenant.name || null, address: editTenant.address || null,
      city: editTenant.city || null, state: editTenant.state || null,
      zip: editTenant.zip || null, property: editTenant.property || null,
      property_type: editTenant.property_type || null, email: editTenant.email || null,
      phone: editTenant.phone || null, lease_start: editTenant.lease_start || null,
      lease_end: editTenant.lease_end || null,
      rent: editTenant.rent ? Math.round(parseFloat(editTenant.rent)) : null,
      status: editTenant.status || null, notes: editTenant.notes || null,
      is_complimentary: editTenant.is_complimentary || false,
      complimentary_notes: editTenant.complimentary_notes || null,
      tenant_type: editTenant.tenant_type || "standard",
      non_paying_subtype: editTenant.non_paying_subtype || null,
      expense_notes: editTenant.expense_notes || null,
    };
    const id = editTenant.id;
    let query = supabase.from("tenants").update(payload);
    if (id !== undefined && id !== null) { query = query.eq("id", id); } else { query = query.eq("name", editTenant.name); }
    const { error } = await query;
    if (error) { alert("Error: " + error.message); return; }
    setEditTenant(null);
    setSelectedTenant(null);
    reload();
  };

  // ── PHOTOS ───────────────────────────────────────────────────────────────────
  const uploadTenantPhoto = async (tenantName) => {
    if (!photoUploadFile || !tenantName) return;
    setPhotoUploading(true);
    const ext = photoUploadFile.name.split(".").pop();
    const fileName = `${landlordId}/${tenantName.replace(/\s+/g, "_")}/${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase.storage.from("tenant-photos").upload(fileName, photoUploadFile);
    if (uploadError) { alert("Upload failed: " + uploadError.message); setPhotoUploading(false); return; }
    const { data: urlData } = supabase.storage.from("tenant-photos").getPublicUrl(fileName);
    const { error: dbError } = await supabase.from("tenant_photos").insert([{
      user_id: landlordId, tenant: tenantName,
      photo_url: urlData.publicUrl, photo_type: photoType,
      label: photoLabel.trim() || null,
    }]);
    if (dbError) { alert("Error saving photo record: " + dbError.message); }
    setPhotoUploadFile(null);
    setPhotoLabel("");
    setPhotoUploading(false);
    reload();
  };

  const deleteTenantPhoto = async (id, photoUrl) => {
    if (!window.confirm("Delete this photo?")) return;
    const pathMatch = photoUrl.match(/tenant-photos\/(.+)$/);
    if (pathMatch) await supabase.storage.from("tenant-photos").remove([pathMatch[1]]);
    await supabase.from("tenant_photos").delete().eq("id", id);
    reload();
  };

  // ── RENT INCREASES ────────────────────────────────────────────────────────────
  const addRentIncrease = async () => {
    if (!newRentIncrease.tenant || !newRentIncrease.effective_date || !newRentIncrease.new_rent) return;
    setShowAddRentIncrease(false);
    const { error } = await supabase.from("rent_increases").insert([{
      user_id: landlordId, tenant: newRentIncrease.tenant,
      effective_date: newRentIncrease.effective_date,
      new_rent: Math.round(parseFloat(newRentIncrease.new_rent)),
      notes: newRentIncrease.notes || null, applied: false,
    }]);
    if (error) { alert("Error: " + error.message); return; }
    setNewRentIncrease({ tenant: "", effective_date: "", new_rent: "", notes: "" });
    reload();
  };

  const applyRentIncrease = async (ri, method = "manual") => {
    if (method === "manual") {
      if (!window.confirm(`Apply rent increase for ${ri.tenant} to $${Number(ri.new_rent).toLocaleString()}/mo? This will update their tenant record.`)) return;
    }
    const tenant = tenants.find(t => t.name === ri.tenant);
    if (!tenant) { if (method === "manual") alert("Tenant not found"); return; }
    const now = new Date().toISOString();
    await supabase.from("tenants").update({ rent: ri.new_rent }).eq("id", tenant.id);
    await supabase.from("rent_increases").update({ applied: true, applied_at: now }).eq("id", ri.id);
    await supabase.from("rent_increase_audit").insert([{
      user_id: landlordId, tenant: ri.tenant,
      old_rent: tenant.rent, new_rent: ri.new_rent,
      effective_date: ri.effective_date, applied_at: now, method,
    }]).then(({ error }) => { if (error) console.warn("Audit log failed:", error.message); });
    reload();
  };

  const deleteRentIncrease = async (id) => {
    if (!window.confirm("Remove this scheduled rent increase?")) return;
    await supabase.from("rent_increases").delete().eq("id", id);
    reload();
  };

  return {
    tenants, setTenants,
    searchTenant, setSearchTenant,
    selectedTenant, setSelectedTenant,
    showAddTenant, setShowAddTenant,
    newTenant, setNewTenant,
    editTenant, setEditTenant,
    showTenantHistory, setShowTenantHistory,
    tenantPhotos, setTenantPhotos,
    photoType, setPhotoType, photoLabel, setPhotoLabel,
    photoUploadFile, setPhotoUploadFile, photoUploading,
    rentIncreases, setRentIncreases,
    showAddRentIncrease, setShowAddRentIncrease,
    newRentIncrease, setNewRentIncrease,
    addTenant, saveTenantEdit, deleteTenant,
    uploadTenantPhoto, deleteTenantPhoto,
    addRentIncrease, applyRentIncrease, deleteRentIncrease,
  };
}
